import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import { formatTimestamp } from "../../../../../utils/utils";
import useReconnectingWebSocket from "../../../../../hooks/useReconnectingWebSocket";
import styles from "./firmware_section.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";

const CONNECTIVITY_PROFILES = [
  {
    id: "stable",
    nameKey: "firmware.profileStable",
    descKey: "firmware.profileStableDesc",
    params: {
      online_threshold_seconds: 60,
      min_stable_online_seconds: 10,
      chunk_window_size: 8,
      ack_timeout_seconds: 60,
      max_offline_wait_seconds: 3600,
    },
  },
  {
    id: "intermittent",
    nameKey: "firmware.profileIntermittent",
    descKey: "firmware.profileIntermittentDesc",
    params: {
      online_threshold_seconds: 120,
      min_stable_online_seconds: 10,
      chunk_window_size: 12,
      ack_timeout_seconds: 90,
      max_offline_wait_seconds: 7200,
    },
  },
  {
    id: "mine",
    nameKey: "firmware.profileMine",
    descKey: "firmware.profileMineDesc",
    params: {
      online_threshold_seconds: 180,
      min_stable_online_seconds: 15,
      chunk_window_size: 16,
      ack_timeout_seconds: 120,
      max_offline_wait_seconds: 14400,
    },
  },
  {
    id: "custom",
    nameKey: "firmware.profileCustom",
    descKey: "firmware.profileCustomDesc",
    params: {},
  },
];

export default function FirmwareSection({ pdui_id, pduiData }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState("stable");
  const [autoApply, setAutoApply] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [shownUploads, setShownUploads] = useState(10);
  const [activeUpload, setActiveUpload] = useState(null);
  const [customParams, setCustomParams] = useState({
    online_threshold_seconds: 60,
    min_stable_online_seconds: 10,
    chunk_window_size: 8,
    ack_timeout_seconds: 60,
    max_offline_wait_seconds: 3600,
    max_retries_per_window: 5,
  });
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  // El handler del WebSocket lee el upload activo de aquí para no tener que
  // reconectar cada vez que cambia.
  const activeUploadRef = useRef(activeUpload);
  activeUploadRef.current = activeUpload;

  // Compute device online status from last_heartbeat
  const lastHeartbeat = pduiData?.last_heartbeat;
  const secondsSinceHeartbeat = lastHeartbeat
    ? Math.floor((Date.now() - new Date(lastHeartbeat).getTime()) / 1000)
    : null;
  const isOnline = secondsSinceHeartbeat !== null && secondsSinceHeartbeat < 180;

  const loadUploads = useCallback(() => {
    axiosInstance
      .get(`pdui/${pdui_id}/firmware-uploads/`)
      .then((res) => {
        setUploads(res.data || []);
        const active = (res.data || []).find((u) => !u.is_terminal);
        setActiveUpload(active || null);
      })
      .catch(() => {});
  }, [pdui_id]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  // Poll active upload status every 3s while there's an active transfer
  useEffect(() => {
    if (!activeUpload || activeUpload.is_terminal) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const res = await axiosInstance.get(
          `pdui/${pdui_id}/firmware-upload/${activeUpload.upload_id}/status/`
        );
        setActiveUpload(res.data);
        if (res.data.is_terminal) {
          loadUploads();
        }
      } catch (e) {}
    }, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [activeUpload, pdui_id, loadUploads]);

  // Progreso en tiempo real de la transferencia. Antes se abría y cerraba
  // con cada cambio de activeUpload y no volvía si se caía.
  useReconnectingWebSocket({
    path: `/ws/pdui/${pdui_id}/firmware/`,
    pduiId: Number(pdui_id),
    label: "WebSocket de firmware",
    onMessage: (data) => {
      const current = activeUploadRef.current;
      if (data.type === "firmware_progress" && current && data.upload_id === current.upload_id) {
        // Merge WS payload with current active upload
        setActiveUpload((prev) => (prev ? { ...prev, ...data.payload } : prev));
      }
    },
  });

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith(".zip")) {
      setMessage({ type: "error", text: t('firmware.onlyZip') });
      return;
    }
    if (!isOnline) {
      setMessage({
        type: "error",
        text: t('firmware.deviceOffline'),
      });
      return;
    }

    setUploading(true);
    setMessage({ type: "info", text: t('firmware.uploading') });

    const selectedProfile = CONNECTIVITY_PROFILES.find((p) => p.id === profile);
    const params = profile === "custom" ? customParams : selectedProfile.params;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("auto_apply", autoApply ? "true" : "false");
    for (const [key, val] of Object.entries(params)) {
      formData.append(key, String(val));
    }

    try {
      const res = await axiosInstance.post(
        `pdui/${pdui_id}/firmware-upload/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
      );
      setMessage({
        type: "success",
        text: t('firmware.transferStarted', { id: res.data.upload_id }),
      });
      setActiveUpload(res.data);
      loadUploads();
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.error === "device_offline") {
        setMessage({
          type: "error",
          text: errData.message || "Dispositivo offline",
        });
      } else if (errData?.error === "upload_already_active") {
        setMessage({
          type: "warning",
          text: errData.message || t('firmware.alreadyActive'),
        });
      } else {
        setMessage({
          type: "error",
          text: errData?.error || err.message || t('firmware.uploadError'),
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = async (uploadId) => {
    if (!window.confirm(t('firmware.cancelConfirm'))) return;
    try {
      await axiosInstance.post(`pdui/${pdui_id}/firmware-upload/${uploadId}/cancel/`);
      loadUploads();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Error" });
    }
  };

  const retryUpload = async (uploadId) => {
    try {
      await axiosInstance.post(`pdui/${pdui_id}/firmware-upload/${uploadId}/retry/`);
      loadUploads();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Error" });
    }
  };

  const applyUpload = async (uploadId) => {
    if (!window.confirm(t('firmware.applyConfirm'))) return;
    try {
      await axiosInstance.post(`pdui/${pdui_id}/firmware-upload/${uploadId}/apply/`);
      loadUploads();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Error" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!isOnline || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const statusPillClass = (status) => {
    const map = {
      pending: styles.pillSending,
      sending: styles.pillSending,
      waiting_device: styles.pillWaiting,
      verifying: styles.pillSending,
      ready_to_apply: styles.pillReady,
      applying: styles.pillSending,
      completed: styles.pillCompleted,
      error: styles.pillError,
      cancelled: styles.pillCancelled,
    };
    return `${styles.statusPill} ${map[status] || styles.pillSending}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>{t('firmware.title')}</div>
        <div className={`${styles.statusBadge} ${isOnline ? styles.statusOnline : styles.statusOffline}`}>
          <div className={`${styles.statusDot} ${isOnline ? styles.online : styles.offline}`}></div>
          {isOnline
            ? `Online (ultimo heartbeat: ${secondsSinceHeartbeat}s)`
            : secondsSinceHeartbeat !== null
            ? `Offline (ultimo: ${Math.floor(secondsSinceHeartbeat / 60)} min)`
            : "Sin heartbeat"}
        </div>
      </div>

      {message && (
        <div
          className={`${styles.alert} ${
            styles["alert" + message.type.charAt(0).toUpperCase() + message.type.slice(1)]
          }`}
        >
          {message.text}
        </div>
      )}

      {!isOnline && (
        <div className={`${styles.alert} ${styles.alertWarning}`}>
          {t('firmware.offlineWarning')}
        </div>
      )}

      {/* Active transfer progress */}
      {activeUpload && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>{t('firmware.activeTransfer')}</div>
          <div className={styles.statusText}>
            <strong>{activeUpload.filename}</strong> -{" "}
            <span className={statusPillClass(activeUpload.status)}>
              {activeUpload.status}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${activeUpload.progress_percent || 0}%` }}
            ></div>
            <div className={styles.progressText}>
              {(activeUpload.acked_count || 0)}/{activeUpload.total_chunks} chunks (
              {activeUpload.progress_percent || 0}%)
            </div>
          </div>
          {activeUpload.error_message && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              {activeUpload.error_message}
            </div>
          )}
          {activeUpload.status === "waiting_device" && (
            <div className={`${styles.alert} ${styles.alertWarning}`}>
              Esperando reconexion del dispositivo. Max espera:{" "}
              {Math.floor(activeUpload.max_offline_wait_seconds / 60)} min
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {activeUpload.status === "ready_to_apply" && (
              <button
                className={`${styles.button} ${styles.buttonOrange}`}
                onClick={() => applyUpload(activeUpload.upload_id)}
              >
                Aplicar actualizacion
              </button>
            )}
            {["error", "waiting_device", "cancelled"].includes(activeUpload.status) && (
              <button
                className={styles.button}
                onClick={() => retryUpload(activeUpload.upload_id)}
              >
                Reintentar
              </button>
            )}
            {!activeUpload.is_terminal && (
              <button
                className={`${styles.button} ${styles.buttonDanger}`}
                onClick={() => cancelUpload(activeUpload.upload_id)}
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Upload form */}
      {!activeUpload && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>{t('firmware.newUpdate')}</div>

          <div className={styles.label}>{t('firmware.connectivityProfile')}</div>
          <div className={styles.profileGrid}>
            {CONNECTIVITY_PROFILES.map((p) => (
              <div
                key={p.id}
                className={`${styles.profileCard} ${profile === p.id ? styles.selected : ""}`}
                onClick={() => setProfile(p.id)}
              >
                <div className={styles.profileName}>{t(p.nameKey)}</div>
                <div className={styles.profileDesc}>{t(p.descKey)}</div>
              </div>
            ))}
          </div>

          {profile === "custom" && (
            <div className={styles.paramsGrid}>
              {Object.entries(customParams).map(([key, val]) => (
                <div key={key} className={styles.formGroup}>
                  <label className={styles.label}>{key}</label>
                  <input
                    className={styles.input}
                    type="number"
                    value={val}
                    onChange={(e) =>
                      setCustomParams({ ...customParams, [key]: Number(e.target.value) })
                    }
                  />
                </div>
              ))}
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={autoApply}
                onChange={(e) => setAutoApply(e.target.checked)}
              />
              {t('firmware.autoApply')}
            </label>
          </div>

          <div
            className={`${styles.dropZone} ${dragging ? styles.dragging : ""} ${
              !isOnline || uploading ? styles.disabled : ""
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (isOnline && !uploading) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => {
              if (isOnline && !uploading) fileInputRef.current?.click();
            }}
          >
            <div className={styles.dropIcon}>
              <FontAwesomeIcon icon={faUpload} />
            </div>
            <div style={{ fontWeight: 500 }}>
              {uploading
                ? t('firmware.uploadingState')
                : !isOnline
                ? t('firmware.offlineDisabled')
                : t('firmware.dragOrClick')}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        </div>
      )}

      {/* History */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>Historial</div>
        {uploads.length === 0 ? (
          <div style={{ color: "#6B7280", fontSize: 13 }}>
            No hay actualizaciones previas
          </div>
        ) : (
          <ul className={styles.uploadList}>
            {uploads.slice(0, shownUploads).map((u) => (
              <li key={u.upload_id} className={styles.uploadItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{u.filename}</div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>
                    {formatTimestamp(u.created_at)} -{" "}
                    {Math.round(u.size_bytes / 1024)} KB - {u.progress_percent}%
                  </div>
                </div>
                <span className={statusPillClass(u.status)}>{u.status}</span>
              </li>
            ))}
          </ul>
        )}
        {uploads.length > shownUploads && (
          <button
            type="button"
            className={styles.button}
            style={{ marginTop: 8 }}
            onClick={() => setShownUploads((n) => n + 10)}
          >
            {t("firmware.showMore", { n: uploads.length - shownUploads })}
          </button>
        )}
      </div>
    </div>
  );
}
